import { etagFromRow, get, getVersioned, post, postVersioned, putVersioned, request, seg } from '../client';
import type { Page } from '../types/common';
import type {
  CompleteUploadRequest, CreateFormRequest, CreateFormResponseRequest, CreateFormVersionRequest, DocumentRecord, FormDefinition, FormGovernanceCommand,
  FormGovernanceCommandRequest, FormResponse, FormStatus, FormSummary, PrepareUploadRequest, PrepareUploadResponse, UploadBytesResponse,
} from '../types/documents';

type Versioned<T> = { data: T; etag: string | null };
const withRowTag = <T extends { rowVersion?: number }>(r: Versioned<T>): Versioned<T> => ({ data: r.data, etag: r.etag ?? etagFromRow(r.data?.rowVersion) });

/** Caller's ETag, else the aggregate's current one (GET) — commands never go out without If-Match (R-005). */
async function currentTag(path: string, etag: string | null | undefined): Promise<string | null> {
  if (etag) return etag;
  const r = await getVersioned<{ rowVersion?: number }>(path);
  return r.etag ?? etagFromRow(r.data?.rowVersion);
}

/** Older mock rows may lack audiences; the backend always sends them. */
export function normalizeForm<T extends FormSummary>(raw: T): T {
  return { ...raw, audiences: raw.audiences?.length ? raw.audiences : [raw.audience] };
}

export const documentsApi = {
  /** Step 1. Returns the pending document's rowVersion: the If-Match for the byte PUT. */
  prepareUpload: (body: PrepareUploadRequest) => post<PrepareUploadResponse>('/documents:prepare-upload', body, { idempotent: true }),
  /**
   * Step 2. PUT raw bytes to the upload token with If-Match `W/"<prepare rowVersion>"`. Returns the next ETag,
   * which is the If-Match for :complete-upload.
   */
  upload: async (token: string, file: Blob | ArrayBuffer, etag?: string | null, contentType?: string): Promise<Versioned<UploadBytesResponse | undefined>> => {
    const type = contentType ?? ((file as Blob).type || 'application/octet-stream');
    const r = await putVersioned<UploadBytesResponse | undefined>(`/documents/uploads/${seg(token)}`, file, {
      ifMatch: etag ?? null,
      headers: { 'Content-Type': type },
    });
    return { data: r.data, etag: r.etag ?? etagFromRow(r.data?.rowVersion) };
  },
  /** Step 3. Needs the ETag returned by the byte PUT (falls back to the document's current ETag). */
  completeUpload: async (id: string, body: CompleteUploadRequest, etag?: string | null) =>
    post<DocumentRecord>(`/documents/${seg(id)}:complete-upload`, body, { ifMatch: await currentTag(`/documents/${seg(id)}`, etag), idempotent: true }),
  list: (patientId: string) => get<Page<DocumentRecord>>(`/patients/${seg(patientId)}/documents`),
  get: (id: string) => get<DocumentRecord>(`/documents/${seg(id)}`),
  /** Only when status=available (scan clean). */
  content: async (id: string) => (await request<Blob>('GET', `/documents/${seg(id)}/content`, { responseType: 'blob' })).data,
  /** If-Match `W/"<rowVersion>"` from the list row (or the detail ETag). */
  release: async (id: string, etag?: string | null) =>
    post<DocumentRecord>(`/documents/${seg(id)}:release`, {}, { ifMatch: await currentTag(`/documents/${seg(id)}`, etag), idempotent: true }),
};

export const formsApi = {
  list: async (p: { audience?: string; status?: FormStatus | string; limit?: number; cursor?: string } = {}) => {
    const page = await get<Page<FormSummary>>('/forms', { ...p });
    return { ...page, items: page.items.map(normalizeForm) };
  },
  get: async (id: string) => normalizeForm(await get<FormDefinition>(`/forms/${seg(id)}`)),
  /** Form definition with its ETag (governance commands need If-Match). */
  getVersioned: async (id: string) => {
    const r = withRowTag(await getVersioned<FormDefinition>(`/forms/${seg(id)}`));
    return { data: normalizeForm(r.data), etag: r.etag };
  },
  /** Form responses for one patient (workforce review queue for FORM-001). */
  responses: (patientId: string, p: { status?: string } = {}) => get<Page<FormResponse>>('/form-responses', { patientId, ...p }),
  respond: async (body: CreateFormResponseRequest) => withRowTag(await postVersioned<FormResponse>('/form-responses', body, { idempotent: true })),
  saveResponse: async (id: string, answers: FormResponse['answers'], etag?: string | null) =>
    withRowTag(await putVersioned<FormResponse>(`/form-responses/${seg(id)}`, { answers }, { ifMatch: await currentTag(`/form-responses/${seg(id)}`, etag) })),
  submit: async (id: string, etag?: string | null) =>
    post<FormResponse>(`/form-responses/${seg(id)}:submit`, {}, { ifMatch: await currentTag(`/form-responses/${seg(id)}`, etag), idempotent: true }),
  /** Review maps answers into the chart with the reviewer recorded as confirmer. If-Match from the response rowVersion. */
  review: async (id: string, accept: string[], etag?: string | null) =>
    post<FormResponse>(`/form-responses/${seg(id)}:review`, { accept }, { ifMatch: await currentTag(`/form-responses/${seg(id)}`, etag), idempotent: true }),
};

const FORM_STEP_UP: Record<FormGovernanceCommand, string | undefined> = {
  'submit-for-review': undefined, approve: undefined, publish: 'Publish form version', schedule: 'Schedule form version', retire: undefined, rollback: 'Roll back form',
};

/**
 * Governed form content (R-121): POST /forms, POST /forms/{id}/versions (If-Match the FORM ETag), and the lifecycle
 * commands draft → clinical_review → approved → (future_effective) → active → retired | rollback → active.
 * Every command needs If-Match + Idempotency-Key; publish/schedule/rollback need step-up.
 */
export const formGovernanceApi = {
  versions: (id: string, p: { limit?: number; cursor?: string } = {}) => get<Page<{ id: string; formId: string; number: number; version: string; createdAt: string; isCurrent: boolean; isActive: boolean; wasPublished: boolean }>>(`/forms/${seg(id)}/versions`, { limit: 100, ...p }),
  create: async (body: CreateFormRequest) => {
    const r = withRowTag(await postVersioned<FormDefinition>('/forms', body, { idempotent: true }));
    return { data: normalizeForm(r.data), etag: r.etag };
  },
  createVersion: async (id: string, body: CreateFormVersionRequest, etag: string | null) => {
    const r = withRowTag(await postVersioned<FormDefinition>(`/forms/${seg(id)}/versions`, body, { ifMatch: etag, idempotent: true }));
    return { data: normalizeForm(r.data), etag: r.etag };
  },
  command: async (id: string, command: FormGovernanceCommand, etag: string | null, body: FormGovernanceCommandRequest = {}) => {
    const r = withRowTag(
      await postVersioned<FormDefinition>(`/forms/${seg(id)}:${command}`, body, { ifMatch: etag, idempotent: true, stepUpLabel: FORM_STEP_UP[command] }),
    );
    return { data: normalizeForm(r.data), etag: r.etag };
  },
};
